import React, { useState, useEffect, useContext, useCallback } from "react";
import WelcomeScreen from "./valuesMapping/WelcomeScreen";
import ValuesSelectionScreen from "./valuesMapping/ValuesSelectionScreen";
import Top3SelectionScreen from "./valuesMapping/Top3SelectionScreen";
import ValuesCompletion from "./valuesMapping/ValuesCompletion";
import { progressService } from "../../../../../services/progressService";
import { UserContext } from "../../../../../UserContext";

const ValuesMapping = ({ userId, subsectionId, initialData, onComplete, onToggleImmersive, onSubsectionChange, }) => {
  const [phase, setPhase] = useState("welcome");
  const [selectedValues, setSelectedValues] = useState([]);
  const [top3Values, setTop3Values] = useState([]);
  const [currentIdentityData, setCurrentIdentityData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const { user } = useContext(UserContext);
  const userIdContext = user?.user_id;
  const userName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.firstName || user?.lastName || "User";
  const profileImage = user?.profilePicture || null;

  // UUID validation helper
  const isValidUUID = (uuid) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  };

  // Fetch Current Identity data (Module 1 - Current You)
  useEffect(() => {
    const fetchCurrentIdentity = async () => {
      if (!userIdContext) return;
      
      try {
        const CURRENT_IDENTITY_SUBSECTION_ID = "f90552a1-12fd-4f0b-b4f5-17e258b84b97";
        
        if (!isValidUUID(CURRENT_IDENTITY_SUBSECTION_ID)) return;

        const record = await progressService.getUserProgress(userIdContext, CURRENT_IDENTITY_SUBSECTION_ID);
        
        if (record?.data) {
          setCurrentIdentityData(record.data);
        }
      } catch (error) {
        console.error("Error fetching Current Identity data:", error);
      }
    };

    fetchCurrentIdentity();
  }, [userIdContext]);

  // Load Values Mapping progress from DB
  const loadProgress = useCallback(async () => {
    if (!userIdContext || !subsectionId || !isValidUUID(subsectionId)) return;
    
    try {
      const record = await progressService.getUserProgress(userIdContext, subsectionId);
      
      if (record?.data) {
        // ✅ FIXED: Handle nested payload structure
        const payload = record.data.payload || record.data;
        setSelectedValues(payload.selectedValues || []);
        setTop3Values(payload.top3Values || []);
      }
    } catch (err) {
      console.error("Failed to load progress:", err);
    }
  }, [userIdContext, subsectionId]);

  // Load progress on mount
  useEffect(() => {
    if (initialData) {
      // ✅ FIXED: Handle nested payload structure
      const payload = initialData.payload || initialData;
      setSelectedValues(payload.selectedValues || []);
      setTop3Values(payload.top3Values || []);
    }
    
    if (userIdContext && subsectionId && isValidUUID(subsectionId)) {
      loadProgress();
    }
  }, [userIdContext, subsectionId, loadProgress, initialData]);

  const handleStart = () => setPhase("main");

  const handleFinishSelection = (values) => {
    setSelectedValues(values);
    setPhase("top3");
  };

  // Save to DB
  const saveProgressSilently = useCallback(async (values, top3 = []) => {
    if (!userIdContext || !subsectionId || !isValidUUID(subsectionId)) return;
    
    setIsSaving(true);
    try {
      // ✅ FIXED: Add type: "widget" to match pattern
      const dataToSave = {
        type: "widget",
        payload: {
          selectedValues: values,
          top3Values: top3,
        }
      };
      
      await progressService.saveProgress(
        userIdContext, 
        subsectionId, 
        dataToSave, 
        top3.length === 3 ? "completed" : "in_progress"
      );
    } catch (err) {
      console.error("Auto-save error:", err);
    } finally {
      setIsSaving(false);
    }
  }, [userIdContext, subsectionId]);

  useEffect(() => {
    if (phase === "welcome" || !userIdContext || !subsectionId) return;
    
    const hasData = selectedValues.length > 0;
    if (!hasData) return;

    const timer = setTimeout(() => {
      saveProgressSilently(selectedValues, top3Values);
    }, 2000);

    return () => clearTimeout(timer);
  }, [selectedValues, top3Values, phase, userIdContext, subsectionId, saveProgressSilently]);

  const handleFinishTop3 = async (top3) => {
    setTop3Values(top3);
    setIsLoading(true);
    setError(null);

    try {
      // ✅ FIXED: Add type: "widget" to match pattern
      const dataToSave = {
        type: "widget",
        payload: {
          selectedValues: selectedValues,
          top3Values: top3,
        }
      };

      await progressService.saveProgress(
        userIdContext,
        subsectionId,
        dataToSave,
        "completed"
      );
      
      setPhase("completion");
    } catch (err) {
      console.error("Error saving values mapping:", err);
      setError(err.message || "Failed to save values mapping");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (typeof onToggleImmersive === "function") {
      if (phase === "welcome") onToggleImmersive(false);
      if (phase === "main" || phase === "top3") onToggleImmersive(true);
      if (phase === "completion") onToggleImmersive(false);
    }
  }, [phase, onToggleImmersive]);

  return (
    <div className="w-full h-full flex flex-col items-center justify-center">
      {phase === "welcome" && <WelcomeScreen onStart={handleStart} />}
      
      {phase === "main" && (
        <ValuesSelectionScreen 
          onFinish={handleFinishSelection} 
          onToggleImmersive={onToggleImmersive}
          initialSelected={selectedValues}
          isSaving={isSaving}
        />
      )}
      
      {phase === "top3" && (
        <Top3SelectionScreen
          selectedValues={selectedValues}
          initialTop3={top3Values}
          onFinish={handleFinishTop3}
          onToggleImmersive={onToggleImmersive}
          loading={isLoading}
          isSaving={isSaving}
        />
      )}

      {phase === "completion" && (
        <ValuesCompletion
          top3Values={top3Values}
          userName={userName}
          profileImage={profileImage}
          currentIdentityData={currentIdentityData}
          userId={userId}
          subsectionId={subsectionId}
          onSubsectionChange={onSubsectionChange}
          onNext={onComplete}
        />
      )}
      
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50">
          {error}
        </div>
      )}
    </div>
  );
};

export default ValuesMapping;